// Phase 74 / 75: bundle add-on section. For each TemplateBundleMember
// wired on the matched template:
//   - mandatory members (optional === false): rendered as a static
//     "Included in your set" tick row, no checkbox.
//   - optional members (optional === true, the default): rendered as
//     an opt-in checkbox row with the member's live price. Customer
//     toggles to include in the cart.
//
// Prices come from the parent App's `bundleMemberPrices` map, which
// is populated by parallel /products/<handle>.js fetches at mount.
// Until prices load, the price slot shows a quiet "$..." placeholder
// rather than blocking the section from rendering.
//
// The parent owns the `selectedBundleIds` Set state; this component
// just renders + emits toggle events.
//
// Phase 80: optional `discountByMemberId` prop. When a row's id is
// present in the map (value 1-100), render the price as strike-through
// (was) + discounted (now) + a small "-15%" pill. Pure presentational;
// the parent owns all the discount maths.
//
// Phase 81: inline variant picker. When a ticked member has more than
// one variant, the row expands to show a swatch grid; selecting a
// variant collapses the row and shows the choice inline. Parent owns
// the pick map + the expanded-row id + the cached product JSON.
//
// Phase 81 follow-up: the picker now splits by option axis (Colour
// row + Size row) instead of one flat grid of every variant combo.
// A flat grid on a colour x size product spits out 50+ tiles - too
// noisy. Per-axis rows + auto-commit when all axes have a pick.

import * as React from "react";
import type { BundleMember, Config } from "../types";
import type { ProductJsonShape } from "../api";

export function BundlePreviewSection({
  config,
  selectedIds,
  prices,
  discountByMemberId,
  productData,
  variantPicks,
  expandedId,
  onPickVariant,
  onToggleExpand,
  onToggle,
}: {
  config: Config;
  selectedIds: Set<string>;
  /** Map of bundle member id -> price in cents (the variant's price,
   *  honouring variantGid override). null = not yet fetched. */
  prices: Record<string, number | null>;
  /** Phase 80: optional discount percent (0-100) per member id.
   *  Renders strike-through pricing when > 0. Defaults to {} when
   *  the parent isn't computing discounts (live flag off). */
  discountByMemberId?: Record<string, number>;
  /** Phase 81: full /products/<handle>.js shape per member - drives
   *  the inline variant picker. Empty/missing = picker doesn't render
   *  (row tick auto-uses fallback variant). */
  productData?: Record<string, ProductJsonShape | null>;
  /** Phase 81: customer's variant pick per member (numeric id, string). */
  variantPicks?: Record<string, string>;
  /** Phase 81: which row is currently expanded (one at a time). */
  expandedId?: string | null;
  /** Phase 81: emit when the customer picks a variant for a member. */
  onPickVariant?: (memberId: string, variantId: string) => void;
  /** Phase 81: emit when the customer clicks "Change" or the row
   *  header to manually toggle the picker open/closed. */
  onToggleExpand?: (memberId: string) => void;
  onToggle: (memberId: string) => void;
}) {
  const members = config.bundleMembers ?? [];
  if (members.length === 0) return null;

  const discounts = discountByMemberId ?? {};
  const data = productData ?? {};
  const picks = variantPicks ?? {};

  return (
    <section className="rk-pc-bundle-preview">
      <div className="rk-pc-bundle-preview__head">
        <span className="rk-pc-bundle-preview__icon" aria-hidden>
          &#10047;
        </span>
        <span className="rk-pc-bundle-preview__title">Add to your set</span>
      </div>
      <ul className="rk-pc-bundle-preview__list">
        {members.map((m) => (
          <BundleRow
            key={m.id}
            member={m}
            selected={selectedIds.has(m.id)}
            price={prices[m.id] ?? null}
            discountPct={discounts[m.id] ?? 0}
            productData={data[m.id] ?? null}
            pickedVariantId={picks[m.id] ?? null}
            expanded={expandedId === m.id}
            onPickVariant={onPickVariant}
            onToggleExpand={onToggleExpand}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </section>
  );
}

function BundleRow({
  member,
  selected,
  price,
  discountPct,
  productData,
  pickedVariantId,
  expanded,
  onPickVariant,
  onToggleExpand,
  onToggle,
}: {
  member: BundleMember;
  selected: boolean;
  price: number | null;
  discountPct: number;
  productData: ProductJsonShape | null;
  pickedVariantId: string | null;
  expanded: boolean;
  onPickVariant?: (memberId: string, variantId: string) => void;
  onToggleExpand?: (memberId: string) => void;
  onToggle: (memberId: string) => void;
}) {
  const variants = productData?.variants ?? [];
  // Use the picked variant's price when present so the row label
  // reflects the customer's actual choice (variants can be priced
  // differently across colours/sizes).
  let effectivePrice = price;
  if (pickedVariantId) {
    const v = variants.find((x) => String(x.id) === pickedVariantId);
    if (v && typeof v.price === "number") effectivePrice = v.price;
  }
  const hasPicker = selected && variants.length > 1;
  const pickedVariant = pickedVariantId
    ? variants.find((x) => String(x.id) === pickedVariantId)
    : null;

  // 2026-07-02: sell the cross-sell visually. The full /products/<handle>.js
  // payload (real title + featured image) is already fetched for the variant
  // picker; the rows used to render slug-derived text only.
  const displayTitle =
    productData?.title || member.productTitle || member.productHandle;
  const thumbSrc = productData?.featured_image
    ? `${productData.featured_image}${productData.featured_image.includes("?") ? "&" : "?"}width=96`
    : null;
  const thumb = thumbSrc ? (
    <span className="rk-pc-bundle-preview__thumb" aria-hidden>
      <img src={thumbSrc} alt="" loading="lazy" />
    </span>
  ) : null;

  // Mandatory members render as a non-interactive tick row.
  if (!member.optional) {
    return (
      <li className="rk-pc-bundle-preview__item is-mandatory">
        <span className="rk-pc-bundle-preview__mandatory" aria-hidden>
          &#10003;
        </span>
        {thumb}
        <span className="rk-pc-bundle-preview__qty">
          {member.quantity}&#215;
        </span>{" "}
        <span className="rk-pc-bundle-preview__name">
          {displayTitle}
        </span>
        {!member.propagateDesign && (
          <span className="rk-pc-bundle-preview__plain">
            {" "}(plain, no embroidery)
          </span>
        )}
        <PriceSlot price={effectivePrice} discountPct={discountPct} />
      </li>
    );
  }

  // Optional members render as a clickable checkbox row.
  return (
    <li
      className={`rk-pc-bundle-preview__item is-optional${selected ? " is-selected" : ""}${expanded ? " is-expanded" : ""}`}
    >
      <label className="rk-pc-bundle-preview__toggle">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(member.id)}
          aria-label={`Add ${displayTitle} to your order`}
        />
        <span className="rk-pc-bundle-preview__checkbox" aria-hidden>
          {selected ? "✓" : ""}
        </span>
        {thumb}
        <span className="rk-pc-bundle-preview__qty">
          {member.quantity}&#215;
        </span>{" "}
        <span className="rk-pc-bundle-preview__name">
          {displayTitle}
        </span>
        {!member.propagateDesign && (
          <span className="rk-pc-bundle-preview__plain">
            {" "}(plain, no embroidery)
          </span>
        )}
        <PriceSlot price={effectivePrice} discountPct={discountPct} />
      </label>
      {/* Phase 81: variant picker. Renders only when the row is
          ticked AND the member has more than one variant. Collapses
          inline once a variant is picked, leaving a "X, Y - Change"
          subline. */}
      {hasPicker && !expanded && pickedVariant && (
        <div className="rk-pc-bundle-preview__pick-summary">
          <span className="rk-pc-bundle-preview__pick-label">
            {variantLabel(pickedVariant)}
          </span>
          <button
            type="button"
            className="rk-pc-bundle-preview__pick-change"
            onClick={() => onToggleExpand?.(member.id)}
          >
            Change
          </button>
        </div>
      )}
      {hasPicker && !expanded && !pickedVariant && (
        <div className="rk-pc-bundle-preview__pick-summary is-empty">
          <button
            type="button"
            className="rk-pc-bundle-preview__pick-change"
            onClick={() => onToggleExpand?.(member.id)}
          >
            Pick a colour and size
          </button>
        </div>
      )}
      {hasPicker && expanded && productData && (
        <AxisPickerBody
          memberId={member.id}
          productData={productData}
          pickedVariantId={pickedVariantId}
          onPickVariant={onPickVariant}
        />
      )}
    </li>
  );
}

// Join the variant's option values into a single label. "Black / S"
// reads as "Black, S" - feels less like a file path.
function variantLabel(v: { option1?: string | null; option2?: string | null; option3?: string | null }): string {
  return [v.option1, v.option2, v.option3]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(", ");
}

// Phase 81 follow-up: per-axis picker body. One row per option axis
// (e.g. Colour, Size). Local partial-pick state inside this
// component; once every axis has a value AND a matching variant
// exists, commit via onPickVariant.
//
// Availability: a value tile on axis N is disabled if no variant
// matches the current picks on every OTHER axis + this value on
// axis N. So if the customer picks "Black", only the sizes that
// exist with Black are enabled.
export function AxisPickerBody({
  memberId,
  productData,
  pickedVariantId,
  onPickVariant,
}: {
  memberId: string;
  productData: ProductJsonShape;
  pickedVariantId: string | null;
  onPickVariant?: (memberId: string, variantId: string) => void;
}) {
  const variants = productData.variants ?? [];
  // Resolve axis names. Shopify's /products/<handle>.js gives
  // `options` as either an array of strings or an array of objects
  // with `.name`. Normalise.
  const axisNames: string[] = (productData.options ?? [])
    .map((o) => (typeof o === "string" ? o : o?.name ?? ""))
    .filter((s): s is string => s.length > 0);
  // How many axes are actually in use across variants - some products
  // declare 3 options but only fill option1 / option2.
  const activeAxes: number[] = [];
  for (let i = 1; i <= 3; i++) {
    if (variants.some((v) => typeof (v as Record<string, unknown>)[`option${i}`] === "string")) {
      activeAxes.push(i);
    }
  }
  // Distinct values per axis, in their first-seen order across
  // variants (so the picker preserves the merchant's natural sort).
  const valuesByAxis = new Map<number, string[]>();
  for (const i of activeAxes) {
    const list: string[] = [];
    const seen = new Set<string>();
    for (const v of variants) {
      const val = (v as Record<string, unknown>)[`option${i}`];
      if (typeof val === "string" && !seen.has(val)) {
        seen.add(val);
        list.push(val);
      }
    }
    valuesByAxis.set(i, list);
  }

  // Initialise partial picks from the committed variant if present
  // - so re-opening the picker shows the customer's previous choices
  // pre-filled.
  const initial = (): Record<number, string> => {
    if (!pickedVariantId) return {};
    const v = variants.find((x) => String(x.id) === pickedVariantId);
    if (!v) return {};
    const out: Record<number, string> = {};
    for (const i of activeAxes) {
      const val = (v as Record<string, unknown>)[`option${i}`];
      if (typeof val === "string") out[i] = val;
    }
    return out;
  };
  const [picks, setPicks] = React.useState<Record<number, string>>(initial);

  // Auto-commit: whenever picks change, if every axis has a value AND
  // an exact-match variant exists, push it up to the parent.
  React.useEffect(() => {
    if (activeAxes.some((i) => !picks[i])) return;
    const match = variants.find((v) => {
      for (const i of activeAxes) {
        const val = (v as Record<string, unknown>)[`option${i}`];
        if (val !== picks[i]) return false;
      }
      return true;
    });
    if (match && String(match.id) !== pickedVariantId) {
      onPickVariant?.(memberId, String(match.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks]);

  // For each axis value, decide whether the customer COULD reach a
  // valid variant by picking it (given their other-axis picks). If
  // not, render disabled.
  const isValueAvailable = (axis: number, value: string): boolean => {
    return variants.some((v) => {
      for (const i of activeAxes) {
        const val = (v as Record<string, unknown>)[`option${i}`];
        if (i === axis) {
          if (val !== value) return false;
        } else if (picks[i] && val !== picks[i]) {
          return false;
        }
      }
      return true;
    });
  };

  return (
    <div className="rk-pc-bundle-preview__pick-body">
      {activeAxes.map((i, idx) => {
        const name = axisNames[i - 1] || `Option ${i}`;
        const values = valuesByAxis.get(i) ?? [];
        return (
          <div
            key={i}
            className="rk-pc-bundle-preview__axis"
            // Axis name as the row label, e.g. "Colour" / "Size".
          >
            <div className="rk-pc-bundle-preview__axis-label">
              {name}
              {picks[i] && (
                <span className="rk-pc-bundle-preview__axis-picked">
                  {" "}- {picks[i]}
                </span>
              )}
            </div>
            <div className="rk-pc-bundle-preview__axis-row">
              {values.map((val) => {
                const active = picks[i] === val;
                const available = isValueAvailable(i, val);
                return (
                  <button
                    key={val}
                    type="button"
                    disabled={!available}
                    className={`rk-pc-bundle-preview__axis-chip${active ? " is-active" : ""}${!available ? " is-disabled" : ""}`}
                    onClick={() => {
                      if (!available) return;
                      setPicks((prev) => ({ ...prev, [i]: val }));
                    }}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
            {idx < activeAxes.length - 1 && (
              <div className="rk-pc-bundle-preview__axis-divider" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Phase 80: price slot that strikes through the "was" amount + renders
// the discounted "now" amount + a "-NN%" pill when a discount applies.
// Falls back to the plain "+$X" label when discountPct is 0.
function PriceSlot({
  price,
  discountPct,
}: {
  price: number | null;
  discountPct: number;
}) {
  if (price == null) return null;
  const wasLabel = `+$${(price / 100).toFixed(price % 100 === 0 ? 0 : 2)}`;
  if (discountPct <= 0) {
    return <span className="rk-pc-bundle-preview__price">{wasLabel}</span>;
  }
  const discounted = Math.round(price * (1 - discountPct / 100));
  const nowLabel = `+$${(discounted / 100).toFixed(discounted % 100 === 0 ? 0 : 2)}`;
  return (
    <span className="rk-pc-bundle-preview__price rk-pc-bundle-preview__price--discounted">
      <span className="rk-pc-bundle-preview__price-was">{wasLabel}</span>
      <span className="rk-pc-bundle-preview__price-now">{nowLabel}</span>
      <span className="rk-pc-bundle-preview__save-pill">-{discountPct}%</span>
    </span>
  );
}
